import { CalendarCheck, CalendarDays, ChefHat, ChessKnight, House, ListChecks, Map, Soup, ClipboardList } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type AppName = "chores" | "todo" | "games" | "activities" | "groceries" | "recipes" | "dinner" | "calendar";

export type SwitcherDestination = "home" | AppName;

export interface AppDescriptor {
  title: string;
  description: string;
  Icon: LucideIcon;
  href: string;
  launcherHref?: string;
}

// This order is the single source of truth for the home launcher and switcher.
export const APP_NAMES: readonly AppName[] = ["calendar", "groceries", "recipes", "dinner", "chores", "todo", "games", "activities"];

export const SWITCHER_DESTINATIONS: readonly SwitcherDestination[] = ["home", ...APP_NAMES];

export const apps: Record<SwitcherDestination, AppDescriptor> = {
  home: {
    title: "Home",
    description: "Family app launcher.",
    Icon: House,
    href: "/",
  },
  chores: {
    title: "Chores",
    description: "Keep repeating chores on track.",
    Icon: ListChecks,
    href: "/chores/",
  },
  todo: {
    title: "To-do",
    description: "Keep household tasks in one place.",
    Icon: ClipboardList,
    href: "/todo/",
  },
  games: {
    title: "Games",
    description: "Choose what the family should play next.",
    Icon: ChessKnight,
    href: "/games/",
  },
  activities: {
    title: "Activities",
    description: "Keep a shared list of things to do.",
    Icon: Map,
    href: "/activities/",
  },
  groceries: {
    title: "Groceries",
    description: "Plan the next trip to the store.",
    Icon: CalendarCheck,
    href: "/groceries/",
  },
  recipes: {
    title: "Recipes",
    description: "Collect the meals everyone loves.",
    Icon: ChefHat,
    href: "/recipes/",
  },
  dinner: {
    title: "Dinner",
    description: "Plan the next meal rotation.",
    Icon: Soup,
    href: "/dinner/",
  },
  calendar: {
    title: "Calendar",
    description: "See the family week at a glance.",
    Icon: CalendarDays,
    href: "/calendar/",
  },
};

export function isAppName(value: string): value is AppName {
  return (APP_NAMES as readonly string[]).includes(value);
}

export function appPath(name: AppName): string {
  return apps[name].href;
}

export function currentDestinationFromPath(pathname: string): SwitcherDestination | null {
  const trimmed = pathname.replace(/\/+$/, "") || "/";
  if (trimmed === "/") return "home";
  const firstSegment = trimmed.split("/")[1] ?? "";
  return isAppName(firstSegment) ? firstSegment : null;
}
